import {
  Button,
  Flex,
  Modal,
  Space,
  notification,
  Dropdown,
  Tag,
  Popconfirm,
  Spin,
  Typography,
  Input,
} from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getServiceOrder, updateServiceOrders } from "../../../service";
import styled from "styled-components";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { makeRefund } from "../../../service";
import { MoreOutlined } from "@ant-design/icons";
import { colors } from "../../../styles/theme";
import { useBranchContext } from "../../../contexts/branch-context";
import { currencySymbol } from "../../../utils/currency";

dayjs.extend(utc);
dayjs.extend(timezone);
const Meal = styled.div``;

export const P = styled.p<{
  $size: string;
  $weight: string;
  $height: string;
  $family?: string;
}>`
  margin: 0;
  font-family: ${({ $family = "Lato" }) => $family}, sans-serif;
  font-size: ${({ $size }) => $size}px;
  font-weight: ${({ $weight }) => $weight};
  line-height: ${({ $height }) => $height}px;
`;

export const ServiceDetailsModal: React.FC<any> = ({
  setOpen,
  open,
  reservationId,
  isOrder,
}) => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const [content, setContent] = useState<any>();
  const hideModal = () => {
    setOpen(false);
  };

  useQuery<any, Error>(
    ["/service-order", reservationId],
    () => reservationId && getServiceOrder({ id: reservationId }),
    {
      onSuccess(data) {
        setContent(data);
      },
      enabled: !!open && !!reservationId,
    }
  );

  const query = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(updateServiceOrders, {
    onSuccess: () => {
      query.invalidateQueries([`/service-order`]);
      query.invalidateQueries([`/services`]);
      query.invalidateQueries([`/service-orders-calendar`]);
      notification.success({
        message: t("serviceDetailsModal.statusChangedSuccess"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
    },
    onError: () => {
      notification.error({
        message: t("serviceDetailsModal.statusChangedError"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  const updateStatus = async (status: string) => {
    await mutateAsync({
      id: content.id,
      body: {
        status: status,
      },
    });
  };


  const {
    mutateAsync: refundMutateAsync,
    isLoading: refundMutateIsLoading,
  } = useMutation(makeRefund, {
    onSuccess: () => {
      query.invalidateQueries([`/service-order`]);
      query.invalidateQueries([`/services`]);
      query.invalidateQueries([`/service-orders-calendar`]);
      notification.success({
        message: t("serviceDetailsModal.refundSuccess"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
      Modal.destroyAll();
    },
    onError: () => {
      notification.error({
        message: t("serviceDetailsModal.refundError"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
      Modal.destroyAll();
    },
  });

  if (!content) return null;
  return (
    <>
      <Modal
        title={
          <>
            {t("serviceDetailsModal.reservationNumber", { reservationId })} -{" "}
            {content.status === "REJECTED" ? (
              <span style={{ color: colors.textSecondary }}>{t("serviceDetailsModal.statusRejected")}</span>
            ) : content.status === "ACCEPTED" ? (
              <span style={{ color: colors.success }}>{t("serviceDetailsModal.statusAccepted")}</span>
            ) : (
              <span style={{ color: colors.error }}>{t("serviceDetailsModal.statusPending")}</span>
            )}
          </>
        }
        width={800}
        open={open}
        onCancel={hideModal}
        footer={(_, { CancelBtn }) => (
          <Flex
            justify={content.status !== "REJECTED" ? "space-between" : "end"}
          >
            {content.status === "IN_QUEUE" && (
              <Flex gap={8}>
                <Popconfirm
                  title={t("serviceDetailsModal.rejectReservationTitle")}
                  description={t("serviceDetailsModal.rejectReservationNoRefundDescription")}
                  onConfirm={() => updateStatus("REJECTED")}
                  okText={t("serviceDetailsModal.yes")}
                  cancelText={t("serviceDetailsModal.no")}
                  okButtonProps={{
                    disabled: isLoading,
                    loading: isLoading,
                  }}
                >
                  <Button danger disabled={isLoading} loading={isLoading}>
                    {t("serviceDetailsModal.reject")}
                  </Button>
                </Popconfirm>
                {(content.details.paymentsType === "PRZELEWY24" || content.details.paymentsType === "STRIPE") &&
                  !content.refund && (
                    <Popconfirm
                      title={t("serviceDetailsModal.rejectReservationTitle")}
                      description={t("serviceDetailsModal.rejectReservationWithRefundDescription")}
                      onConfirm={() => {
                        updateStatus("REJECTED");
                      }}
                      okText={t("serviceDetailsModal.yes")}
                      cancelText={t("serviceDetailsModal.no")}
                      okButtonProps={{
                        disabled: isLoading,
                        loading: isLoading,
                      }}
                    >
                      <Button danger disabled={isLoading} loading={isLoading}>
                        {t("serviceDetailsModal.rejectAndRefund")}
                      </Button>
                    </Popconfirm>
                  )}

                <Popconfirm
                  title={t("serviceDetailsModal.acceptReservationTitle")}
                  description={t("serviceDetailsModal.acceptReservationDescription")}
                  onConfirm={() => updateStatus("ACCEPTED")}
                  okText={t("serviceDetailsModal.yes")}
                  okButtonProps={{
                    disabled: isLoading,
                    loading: isLoading,
                  }}
                  cancelText={t("serviceDetailsModal.no")}
                >
                  <Button
                    style={{
                      border: `1px solid ${colors.success}`,
                      color: colors.success,
                    }}
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    {t("serviceDetailsModal.accept")}
                  </Button>{" "}
                </Popconfirm>
              </Flex>
            )}
            {content.status === "ACCEPTED" && (
              <Flex gap={8}>
                <Popconfirm
                  title={t("serviceDetailsModal.rejectReservationTitle")}
                  description={t("serviceDetailsModal.rejectReservationNoRefundDescription")}
                  onConfirm={() => updateStatus("REJECTED")}
                  okText={t("serviceDetailsModal.yes")}
                  cancelText={t("serviceDetailsModal.no")}
                  okButtonProps={{
                    disabled: isLoading,
                    loading: isLoading,
                  }}
                >
                  <Button danger disabled={isLoading} loading={isLoading}>
                    {t("serviceDetailsModal.cancel")}
                  </Button>
                </Popconfirm>
              </Flex>
            )}
            <CancelBtn />
          </Flex>
        )}
        cancelText={t("serviceDetailsModal.close")}
        zIndex={1100}
      >
        <Meal>
          <Space>
            <P $size="14" $weight="600" $height="20">
              {t("serviceDetailsModal.reservingPersonData")}
            </P>
          </Space>

          <Flex
            vertical
            style={{
              marginBottom: "10px",
            }}
          >
            <Space
              style={{
                columnGap: "4px",
              }}
            >
              <span>
                <P $size="14" $height="16" $weight="400" $family="Lato">
                  {t("serviceDetailsModal.fullName")}
                </P>
              </span>
              <span>
                <P $size="14" $height="20" $weight="300" $family="Lato-Light">
                  {content?.clientName}
                </P>
              </span>
            </Space>
            <Space
              style={{
                columnGap: "4px",
              }}
            >
              <span>
                <P $size="14" $height="16" $weight="400" $family="Lato">
                  {t("serviceDetailsModal.phone")}
                </P>
              </span>
              <span>
                <P $size="14" $height="20" $weight="300" $family="Lato-Light">
                  {content?.telNumber}
                </P>
              </span>
            </Space>
            <Space
              style={{
                columnGap: "4px",
              }}
            >
              <span>
                <P $size="14" $height="16" $weight="400" $family="Lato">
                  {t("serviceDetailsModal.email")}
                </P>
              </span>
              <span>
                <P $size="14" $height="20" $weight="300" $family="Lato-Light">
                  {content?.email}
                </P>
              </span>
            </Space>
          </Flex>
          <Space
            style={{
              marginBottom: "10px",
            }}
          >
            <P $size="14" $weight="600" $height="20">
              {isOrder ? t("serviceDetailsModal.order") : t("serviceDetailsModal.reservation")}
            </P>
          </Space>

          <Flex
            vertical
            style={{
              marginBottom: "10px",
            }}
          >
            {content?.details?.cart?.map((control: any) => {
              return (
                <Space
                  style={{
                    columnGap: "4px",
                  }}
                >
                  <span>
                    <P $size="14" $height="16" $weight="400" $family="Lato">
                      {control?.cart_positoin.meal_quantity}
                      {"x "}
                      {control?.cart_positoin.meal_name} -{" "}
                      {(
                        (control?.cart_positoin.meal_quantity *
                          control?.cart_positoin.meal_price) /
                        100
                      ).toFixed(2)}
                      {" " + currencySymbol(currentBranch?.currency)}
                    </P>
                  </span>
                  <span>
                    <P
                      $size="14"
                      $height="20"
                      $weight="300"
                      $family="Lato-Light"
                    >
                      {control?.value}
                    </P>
                  </span>
                </Space>
              );
            })}
          </Flex>
          <Space
            style={{
              marginBottom: "10px",
            }}
          >
            <P $size="14" $weight="600" $height="20">
              {t("serviceDetailsModal.dateTime")}
            </P>
          </Space>

          <Flex
            vertical
            style={{
              marginBottom: "10px",
            }}
          >
            <Space
              style={{
                columnGap: "4px",
              }}
            >
              <span>
                <P $size="14" $height="16" $weight="400" $family="Lato">
                  {dayjs.utc(content?.startTime).format("DD-MM-YYYY HH:mm")}
                </P>
              </span>
            </Space>
          </Flex>
          <Space
            style={{
              marginBottom: "10px",
            }}
          >
            <P $size="14" $weight="600" $height="20">
              {t("serviceDetailsModal.serviceExecution")}
            </P>
          </Space>

          <Flex
            vertical
            style={{
              marginBottom: "10px",
            }}
          >
            {content.resources?.map((resource: any) => {
              return (
                <Space
                  style={{
                    columnGap: "4px",
                  }}
                >
                  {resource.resource.resourceCategory.name +
                    ": " +
                    resource.resource.name}
                </Space>
              );
            })}
          </Flex>
        </Meal>
        {content?.details?.additionalSections.map(
          ({ sectionName, controls }: any) => {
            return (
              <Meal>
                <Space
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  <P $size="14" $weight="600" $height="20">
                    {sectionName}
                  </P>
                </Space>

                <Flex
                  vertical
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  {controls.map((control: any) => {
                    return (
                      <Space
                        style={{
                          columnGap: "4px",
                        }}
                      >
                        <span>
                          <P
                            $size="14"
                            $height="16"
                            $weight="400"
                            $family="Lato"
                          >
                            {control.name}:
                          </P>
                        </span>
                        <span>
                          <P
                            $size="14"
                            $height="20"
                            $weight="300"
                            $family="Lato-Light"
                          >
                            {control.value}
                          </P>
                        </span>
                      </Space>
                    );
                  })}
                </Flex>
              </Meal>
            );
          }
        )}
        <Meal>
          <Flex
            justify="space-between"
            style={{
              marginTop: "20px",
              marginBottom: "10px",
            }}
            align="center"
          >
            <Space>
              <P $size="14" $weight="600" $height="20">
                {t("serviceDetailsModal.payment")}
              </P>
            </Space>
            {(content.details.paymentsType === "PRZELEWY24" || content.details.paymentsType === "STRIPE") && !content.refund && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <div
                          onClick={() =>
                            Modal.info({
                              icon: null,
                              destroyOnClose: true,
                              content: (
                                <Flex
                                  vertical
                                  style={{
                                    paddingBottom: "20px",
                                  }}
                                  justify="center"
                                  align="center"
                                >
                                  <Typography.Title level={5}>
                                    {t("serviceDetailsModal.enterPin")}
                                  </Typography.Title>
                                  <Input.OTP
                                    length={4}
                                    // type="number"
                                    onChange={(e) => {
                                      refundMutateAsync({
                                        productOrderId: content?.id,
                                        pin: e,
                                      });
                                    }}
                                    inputMode="numeric"
                                  />
                                </Flex>
                              ),
                              footer: (
                                <Flex
                                  style={{
                                    width: "100%",
                                  }}
                                  justify="center"
                                  align="center"
                                >
                                  {refundMutateIsLoading ? (
                                    <Spin size="small" />
                                  ) : (
                                    <></>
                                  )}
                                </Flex>
                              ),
                              closable: true,
                            })
                          }
                        >
                          {t("serviceDetailsModal.refundFunds")}
                        </div>
                      ),
                    },
                  ],
                }}
                placement="bottomRight"
                arrow={{ pointAtCenter: true }}
              >
                <Button icon={<MoreOutlined />} size="small" />
              </Dropdown>
            )}
            {content.refund && <Tag style={{ background: colors.errorLight, borderColor: colors.error, color: colors.error }}>{t("serviceDetailsModal.fundsRefunded")}</Tag>}
          </Flex>

          <Flex
            vertical
            style={{
              marginBottom: "10px",
            }}
          >
            <Space
              style={{
                columnGap: "4px",
              }}
            >
              <span>{content?.details?.payments}</span>
            </Space>
          </Flex>
        </Meal>
      </Modal>
    </>
  );
};
